import { useMemo } from "react";

import type { FloorplanOutlet } from "./types";

interface OutletOverlayProps {
  outlets: FloorplanOutlet[];
  /** Current floorplan zoom — used to scale the cluster radius so the
   *  on-screen footprint stays roughly constant. */
  zoom: number;
  /** SVG units the floorplan viewBox is wide. Used together with the
   *  rendered SVG width (in CSS px) to translate the desired on-screen
   *  cluster radius into viewBox units. */
  svgUnitsWide: number;
  /** Approximate CSS pixel radius for clustering — tweak per layer. */
  targetClusterPx?: number;
  /** Width of the rendered SVG in CSS pixels. Pass 0 to fall back to a
   *  conservative default; it'll be re-derived once the parent's
   *  ResizeObserver kicks in. */
  renderedSvgPx?: number;
}

const NETWORK_COLOUR_FALLBACK = "#3b82f6"; // blue when network not resolved

interface OutletCluster {
  cx: number;
  cy: number;
  outlets: FloorplanOutlet[];
}

function clusterOutlets(outlets: FloorplanOutlet[], radius: number): OutletCluster[] {
  const groups: OutletCluster[] = [];
  for (const o of outlets) {
    const g = o.geometry;
    if (!g || g.type !== "Point") continue;
    const [x, y] = g.coordinates;
    const found = groups.find((gr) => Math.hypot(gr.cx - x, gr.cy - y) <= radius);
    if (found) {
      found.outlets.push(o);
      const n = found.outlets.length;
      found.cx = (found.cx * (n - 1) + x) / n;
      found.cy = (found.cy * (n - 1) + y) / n;
    } else {
      groups.push({ cx: x, cy: y, outlets: [o] });
    }
  }
  return groups;
}

function sequentialRange(names: string[]): string | null {
  if (names.length < 2) return null;
  const parsed = names.map((n) => {
    const m = n.match(/^(.*?)(\d+)$/);
    return m ? { prefix: m[1], num: parseInt(m[2], 10), name: n } : null;
  });
  if (parsed.some((p) => p === null)) return null;
  const prefix = parsed[0]!.prefix;
  if (parsed.some((p) => p!.prefix !== prefix)) return null;
  const sorted = parsed.slice().sort((a, b) => a!.num - b!.num) as { num: number; name: string }[];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].num !== sorted[i - 1].num + 1) return null;
  }
  return `${sorted[0].name}–${sorted[sorted.length - 1].name}`;
}

function clusterPrimary(c: OutletCluster) {
  const medium = c.outlets.some((o) => o.medium === "fibre") ? "fibre" : "copper";
  const status = c.outlets.some((o) => o.status === "down") ? "down"
    : c.outlets.every((o) => o.status === "free") ? "free"
    : "connected";
  return { medium, status };
}

function clusterLabel(c: OutletCluster): string {
  if (c.outlets.length === 1) return c.outlets[0].name;
  const names = c.outlets.map((o) => o.name);
  const range = sequentialRange(names);
  if (range) return range;
  const shown = names.slice(0, 2).join(", ");
  const more = names.length > 2 ? ` +${names.length - 2}` : "";
  return shown + more;
}

export function OutletOverlay({
  outlets,
  zoom,
  svgUnitsWide,
  targetClusterPx = 22,
  renderedSvgPx = 0,
}: OutletOverlayProps) {
  const clusterRadius = useMemo(() => {
    if (!renderedSvgPx || !svgUnitsWide) return 16; // conservative default before measure
    const svgUnitsPerPx = svgUnitsWide / renderedSvgPx;
    return (targetClusterPx * svgUnitsPerPx) / Math.max(0.1, zoom);
  }, [renderedSvgPx, svgUnitsWide, zoom, targetClusterPx]);

  const clusters = useMemo(() => clusterOutlets(outlets, clusterRadius), [outlets, clusterRadius]);

  return (
    <g style={{ pointerEvents: "none" }}>
      {clusters.map((c, i) => {
        const primary = clusterPrimary(c);
        const stroke = NETWORK_COLOUR_FALLBACK;
        const opacity = primary.status === "free" ? 0.55 : 1;
        const isFibre = primary.medium === "fibre";
        return (
          <g key={`outlet-cluster-${i}`} transform={`translate(${c.cx}, ${c.cy})`} opacity={opacity}>
            {/* Port body */}
            <rect x={-9} y={-7} width={18} height={14} rx={2} fill="white" stroke={stroke} strokeWidth={2} />
            {isFibre ? (
              <>
                <circle cx={-3.5} cy={0} r={2} fill={stroke} />
                <circle cx={3.5} cy={0} r={2} fill={stroke} />
              </>
            ) : (
              <>
                <rect x={-3} y={-4} width={2} height={6} fill={stroke} />
                <rect x={1} y={-4} width={2} height={6} fill={stroke} />
              </>
            )}
            {primary.status === "down" && (
              <line x1={-12} y1={-10} x2={12} y2={10} stroke="#ef4444" strokeWidth={2} />
            )}
            {/* Count badge for clusters > 1 */}
            {c.outlets.length > 1 && (
              <g transform="translate(8, -8)">
                <circle cx={0} cy={0} r={5.5} fill="#0f172a" stroke="white" strokeWidth={1} />
                <text x={0} y={0.5} textAnchor="middle" dominantBaseline="central" fontSize={7} fontWeight={700} fill="white">
                  {c.outlets.length}
                </text>
              </g>
            )}
            {/* Label */}
            <text x={0} y={16} textAnchor="middle" fontSize={8} fontWeight={500} fill="#334155">
              {clusterLabel(c)}
            </text>
          </g>
        );
      })}
    </g>
  );
}
